import { ArrowRight, ArrowUpRight } from "lucide-react"
import { Link } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { MarketingMetadata } from "@/components/marketing/MarketingMetadata"
import { WORK_INDEX_COPY } from "@/content/marketing-work-index"
import { localizedHomeUrl } from "@/lib/marketing-structured-data"
import type { MarketingPageProps } from "@/types/marketing"
import cajankaWebshop from "../../assets/marketing/projects/cajanka-webshop.webp"
import vanCleefLogo from "../../assets/marketing/clients/vancleef/logo.webp"
import vanCleefLogin from "../../assets/marketing/clients/vancleef/login.webp"

export default function MarketingWork(props: MarketingPageProps) {
  const copy = WORK_INDEX_COPY[props.locale]
  const metricKindLabels = props.locale === "hr"
    ? { measured: "Izmjereno", capacity: "Kapacitet", estimate: "Procjena iz koda" }
    : { measured: "Measured", capacity: "Capacity", estimate: "Code-derived estimate" }
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: props.title,
        description: props.description,
        url: props.canonical_url,
        inLanguage: props.locale === "hr" ? "hr-HR" : "en",
        hasPart: [
          { "@type": "CreativeWork", name: "VanCleef", url: new URL(props.vancleef_href, localizedHomeUrl(props.locale)).toString() },
          { "@type": "CreativeWork", name: "Čajanka", url: new URL(props.cajanka_href, localizedHomeUrl(props.locale)).toString() },
        ],
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: props.locale === "hr" ? "Početna" : "Home", item: localizedHomeUrl(props.locale) },
          { "@type": "ListItem", position: 2, name: props.locale === "hr" ? "Radovi" : "Work", item: props.canonical_url },
        ],
      },
    ],
  }

  return (
    <>
      <MarketingMetadata {...{
        alternateUrls: props.alternate_urls, canonicalUrl: props.canonical_url, description: props.description,
        locale: props.locale, noindex: props.noindex, socialImageUrl: props.social_image_url,
        structuredData, title: props.title,
      }} />
      <MarketingLayout
        consultationHref={props.consultation_href}
        currentPageKey={props.page_key}
        footer={props.footer}
        homeHref={props.home_href}
        languageSwitch={props.language_switch}
        locale={props.locale}
        navigation={props.navigation}
      >
        <section className="work-index-hero">
          <h1 className="type-marketing-hero-display">{copy.hero.heading}</h1>
          <p className="type-marketing-lead">{copy.hero.body}</p>
        </section>

        <article className="work-index-case work-index-vancleef">
          <header>
            <div>
              <p className="type-marketing-caption type-marketing-signal-display">{copy.vancleef.relationship}</p>
              <h2 className="type-marketing-section-display type-marketing-inverse">{copy.vancleef.heading}</h2>
            </div>
            <img alt={props.locale === "hr" ? "Logotip klijenta VanCleef" : "VanCleef client logo"} height="200" src={vanCleefLogo} width="200" />
          </header>
          <div className="work-index-story">
            <p><strong>{props.locale === "hr" ? "Za koga" : "Audience"}</strong>{copy.vancleef.audience}</p>
            <p><strong>{props.locale === "hr" ? "Ograničenje" : "Constraint"}</strong>{copy.vancleef.problem}</p>
            <p><strong>{props.locale === "hr" ? "Izgrađeno" : "Built"}</strong>{copy.vancleef.outcome}</p>
          </div>
          <figure className="work-index-artifact">
            <img alt={props.locale === "hr" ? "Prijava u privatnu VanCleef operativnu platformu" : "Login screen for VanCleef's private operations platform"} height="720" loading="lazy" src={vanCleefLogin} width="1280" />
            <figcaption>{props.locale === "hr" ? "Javno siguran prikaz stvarne privatne platforme" : "Public-safe view of the real private platform"}</figcaption>
          </figure>
          <ol className="work-index-systems">
            {copy.vancleef.systems.map((system, index) => <li key={system}><span>{String(index + 1).padStart(2, "0")}</span>{system}</li>)}
          </ol>
          <ol className="work-index-metrics">
            {copy.vancleef.metrics.map((metric) => <li key={metric.value}><small>{metricKindLabels[metric.kind]}</small><strong>{metric.value}</strong><span>{metric.label}</span></li>)}
          </ol>
          <Button asChild variant="marketing" size="marketingLg"><Link href={props.vancleef_href} data-analytics-id="work-vancleef">{copy.vancleef.link}<ArrowRight className="marketing-inline-icon" aria-hidden="true" /></Link></Button>
        </article>

        <article className="work-index-case work-index-cajanka">
          <header>
            <p className="type-marketing-caption type-marketing-accent-display">{copy.cajanka.relationship}</p>
            <h2 className="type-marketing-section-display">{copy.cajanka.heading}</h2>
          </header>
          <div className="work-index-story">
            <p><strong>{props.locale === "hr" ? "Za koga" : "Audience"}</strong>{copy.cajanka.audience}</p>
            <p><strong>{props.locale === "hr" ? "Ograničenje" : "Constraint"}</strong>{copy.cajanka.problem}</p>
            <p><strong>{props.locale === "hr" ? "Izgrađeno" : "Built"}</strong>{copy.cajanka.outcome}</p>
          </div>
          <figure className="work-index-artifact"><img src={cajankaWebshop} alt={props.locale === "hr" ? "Stvarna Čajanka web-trgovina, rujan 2026." : "The actual Čajanka webshop, September 2026."} width="1440" height="1000" loading="lazy" /></figure>
          <ol className="work-index-systems work-index-systems-light">
            {copy.cajanka.systems.map((system, index) => <li key={system}><span>{String(index + 1).padStart(2, "0")}</span>{system}</li>)}
          </ol>
          <a className="work-index-artifact-link type-marketing-action" href="https://cajanka.hr/" rel="noreferrer" target="_blank">
            {props.locale === "hr" ? "Otvorite javnu Čajanka web-trgovinu" : "Open Čajanka's public webshop"}<ArrowUpRight className="marketing-inline-icon" aria-hidden="true" />
          </a>
          <p className="work-index-disclosure type-marketing-body-small">{copy.cajanka.evidence}</p>
          <Button asChild variant="marketing" size="marketingLg"><Link href={props.cajanka_href} data-analytics-id="work-cajanka">{copy.cajanka.link}<ArrowRight className="marketing-inline-icon" aria-hidden="true" /></Link></Button>
        </article>

        <section className="work-index-final">
          <div><h2 className="type-marketing-section-display">{copy.final.heading}</h2></div>
          <div><p className="type-marketing-prose">{copy.final.body}</p><Button asChild variant="marketing" size="marketingLg"><Link href={props.consultation_href} data-analytics-id="work-consultation">{copy.final.action}<ArrowRight className="marketing-inline-icon" aria-hidden="true" /></Link></Button></div>
        </section>
      </MarketingLayout>
    </>
  )
}
